<template>
  <div class="vendor-schedule-page">
    <v-container class="vendor-schedule-page__container">
      <div class="vendor-schedule-page__content">
        <div class="vendor-schedule-page__header">
          <div class="vendor-schedule-page__header-icon">
            <v-icon size="28" color="primary">mdi-calendar-clock-outline</v-icon>
          </div>
          <h2 class="vendor-schedule-page__title mb-2 ma-0">Schedule a Demo</h2>
          <p class="vendor-schedule-page__subtitle vendor-schedule-page__subtitle--lead">
            Book your personalized demo.
          </p>
          <p class="vendor-schedule-page__subtitle">
            See how we connect your HOA with trusted vendors. Pick a time that
            works. Our team will confirm within one business day.
          </p>
          <v-row class="vendor-schedule-page__benefits" dense>
            <v-col cols="6" class="vendor-schedule-page__benefit-col">
              <div class="vendor-schedule-page__benefit">
                <v-icon size="24" color="primary">mdi-clock-outline</v-icon>
                <span>30 minutes</span>
              </div>
            </v-col>
            <v-col cols="6" class="vendor-schedule-page__benefit-col">
              <div class="vendor-schedule-page__benefit">
                <v-icon size="24" color="primary">mdi-video-outline</v-icon>
                <span>Live demo</span>
              </div>
            </v-col>
          </v-row>
        </div>

        <v-form class="vendor-schedule-page__form" @submit.prevent="handleSubmit">
          <v-row class="vendor-schedule-page__row" dense>
            <v-col cols="12" md="6">
              <AppTextField
                v-model="form.name"
                label="Your Full Name"
                label-icon="mdi-account-outline"
                placeholder="John Smith"
                :rules="[required]"
                autocomplete="name"
                :error-messages="errors.name ? [errors.name] : []"
                @update:modelValue="errors.name = ''"
              />
            </v-col>
            <v-col cols="12" md="6">
              <AppTextField
                v-model="form.email"
                label="Email Address"
                label-icon="mdi-email-outline"
                placeholder="john@example.com"
                :rules="[required, email]"
                autocomplete="email"
                :error-messages="errors.email ? [errors.email] : []"
                @update:modelValue="errors.email = ''"
              />
            </v-col>
            <v-col cols="12" md="6">
              <div>
                <AppMobileField
                  v-model="form.phone"
                  label="Phone Number"
                  label-icon="mdi-phone-outline"
                  placeholder="555 123 4567"
                  :rules="[required, phone]"
                  autocomplete="tel"
                  :error-messages="errors.phone ? [errors.phone] : []"
                  @update:modelValue="errors.phone = ''"
                />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <AppAddressField
                v-model="form.hoaAddress"
                label="HOA Address"
                label-icon="mdi-map-marker-outline"
                placeholder="123 Maple St, Springfield"
                prepend-inner-icon=""
                :rules="[required]"
                autocomplete="street-address"
                :error-messages="errors.hoaAddress ? [errors.hoaAddress] : []"
                @update:modelValue="errors.hoaAddress = ''"
                @selected="handleHoaAddressSelected"
              />
            </v-col>
            <v-col cols="12" md="6">
              <AppDateField
                v-model="form.demoDate"
                label="Preferred Date"
                label-icon="mdi-calendar-outline"
                placeholder="Jun 24, 2026"
                :min-date="tomorrow"
                :rules="[required]"
                :error-messages="errors.demoDate ? [errors.demoDate] : []"
                @update:modelValue="errors.demoDate = ''"
              />
            </v-col>
            <v-col cols="12" md="6">
              <AppTimeField
                v-model="form.demoTime"
                label="Preferred Time"
                label-icon="mdi-clock-outline"
                placeholder="10:30 AM"
                :rules="[required]"
                :error-messages="errors.demoTime ? [errors.demoTime] : []"
                @update:modelValue="errors.demoTime = ''"
              />
            </v-col>
          </v-row>

          <div class="vendor-schedule-page__actions px-2 mb-10">
            <AppButtonFlat
              color="primary"
              block
              type="submit"
              prepend-icon="mdi-calendar"
              :loading="isSubmitting"
              :disabled="isSubmitting"
            >
              Request Demo
            </AppButtonFlat>
          </div>

          <p v-if="submitError" class="vendor-schedule-page__submit-error">
            {{ submitError }}
          </p>
        </v-form>
      </div>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppAddressField from "@/shared/components/ui/AppAddressField.vue";
import AppDateField from "@/shared/components/ui/AppDateField.vue";
import AppTimeField from "@/shared/components/ui/AppTimeField.vue";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import AppMobileField from "@/shared/components/ui/AppMobileField.vue";
import { sendDemoRequest } from "@/shared/services/demo-requests.api";

type HoaAddressSelection = {
  id: number;
  number: string;
  street: string;
  unit: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  latitude: string;
  longitude: string;
  full_address: string;
};

const form = reactive({
  name: "",
  phone: "",
  email: "",
  hoaAddress: "",
  demoDate: null as Date | string | null,
  demoTime: "",
  addressRaw: null as HoaAddressSelection | null,
});

const errors = reactive({
  name: "",
  phone: "",
  email: "",
  hoaAddress: "",
  demoDate: "",
  demoTime: "",
});

const isSubmitting = ref(false);
const submitError = ref("");
const router = useRouter();

const tomorrow = computed(() => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(0, 0, 0, 0);
  return date;
});

const required = (v: unknown) => Boolean(String(v ?? "").trim()) || "Required";
const email = (v: unknown) => /.+@.+\..+/.test(String(v ?? "").trim()) || "Invalid email";
const phone = (v: unknown) => Boolean(String(v ?? "").trim()) || "Invalid phone";

function handleHoaAddressSelected(address: HoaAddressSelection | null) {
  form.addressRaw = address;
  console.log("Selected HOA address:", address);
}

async function handleSubmit() {
  submitError.value = "";

  errors.name = form.name.trim() ? "" : "Required";
  errors.phone = form.phone.trim() ? "" : "Required";
  errors.email = form.email.trim() ? "" : "Required";
  errors.hoaAddress = form.hoaAddress.trim() ? "" : "Required";
  errors.demoDate = form.demoDate ? "" : "Required";
  errors.demoTime = form.demoTime.trim() ? "" : "Required";

  if (Object.values(errors).some(Boolean)) return;

  isSubmitting.value = true;
  try {
    await sendDemoRequest({
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      hoa_address: form.hoaAddress.trim(),
      address_data: form.addressRaw,
      demo_date: formatDemoDate(form.demoDate),
      demo_time: form.demoTime.trim(),
    });

    await router.push({ name: "vendor.schedule.success" });
  } catch (error) {
    submitError.value = "Unable to submit your request. Please try again.";
    console.error("[vendor-schedule] demo request failed", error);
  } finally {
    isSubmitting.value = false;
  }
}

function formatDemoDate(value: Date | string | null) {
  if (!value) return "";
  if (typeof value === "string") return value;

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
</script>

<style scoped lang="scss">
.vendor-schedule-page__container {
  max-width: 1120px;
}

.vendor-schedule-page__content {
  max-width: 920px;
  margin: 0 auto;
}

.vendor-schedule-page__header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 24px;
}

.vendor-schedule-page__header-icon {
  width: 52px;
  height: 52px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: rgba(63, 81, 181, 0.08);
  margin-bottom: 12px;
}

.vendor-schedule-page__title {
  margin: 0;
  color: #0f172a;
  line-height: 1.04;
  letter-spacing: -0.04em;
}

.vendor-schedule-page__subtitle {
  max-width: 56ch;
  color: #475569;
  font-size: 15px;
  line-height: 1.65;
}

.vendor-schedule-page__subtitle--lead {
  max-width: 42ch;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}

.vendor-schedule-page__benefits {
  margin-top: 18px;
  width: 100%;
}

.vendor-schedule-page__benefit-col {
  display: flex;
}

.vendor-schedule-page__benefit {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 100%;
  min-height: 78px;
  padding: 10px 14px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 500;
  line-height: 1.2;
}

.vendor-schedule-page__benefit span {
  text-align: center;
}

.vendor-schedule-page__form {
  display: flex;
  flex-direction: column;
  gap: 26px;
}

.vendor-schedule-page__row {
  margin: 0;
}

.vendor-schedule-page__actions {
  display: flex;
  justify-content: flex-end;
}

.vendor-schedule-page__submit-error {
  margin: -10px 0 0;
  color: #dc2626;
  font-size: 14px;
  line-height: 1.5;
}
</style>
